import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { CheckIcon } from './icons'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
  /** Header "select all" state when some but not all rows are selected. */
  indeterminate?: boolean
  /** Navy fill instead of the consent red — used by table selection. */
  tone?: 'consent' | 'select'
  /**
   * A record of a choice already made, not a control.
   *
   * Rendering it `disabled` would grey the box and the label to the point of illegibility, which
   * is the wrong treatment for the line that says what the participant agreed to.
   */
  readOnlyState?: boolean
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, tone = 'consent', readOnlyState = false, className = '', disabled, ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement | null>(null)

  // `indeterminate` has no HTML attribute; it exists only as a DOM property.
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate
  }, [indeterminate])

  const classes = [
    'checkbox',
    tone === 'select' ? 'checkbox--select' : '',
    readOnlyState ? 'checkbox--static' : '',
    disabled && !readOnlyState ? 'checkbox--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <label className={classes}>
      <input
        {...rest}
        type="checkbox"
        disabled={disabled || readOnlyState}
        aria-readonly={readOnlyState || undefined}
        className="checkbox__input"
        ref={(node) => {
          inner.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
      />
      <span className="checkbox__box" aria-hidden="true">
        <CheckIcon size={12} />
      </span>
      {label && <span className="checkbox__label">{label}</span>}
    </label>
  )
})

export default Checkbox
